import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

/**
 * Unified "Assign to" picker that lets the user pick any combination of teams
 * and individual users in a single grouped dropdown. Mirrors the Image #1 spec:
 * search input header, Invite New Member CTA at the top of the dropdown, then
 * Teams and Users sections with checkboxes.
 *
 * Controlled via {@link onChange}. Selected entries render as chips above the
 * input. The teams list comes from the org-scoped Teams API; users come from
 * the technicians/full-users list the parent already loads for WO assignment.
 */
const initialsOf = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  return trimmed.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
};

const WorkOrderAssignToPicker = ({
  teams = [],
  users = [],
  selectedTeamIds = [],
  selectedUserIds = [],
  onChange,
  invalid = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const teamSelected = useMemo(() => new Set(selectedTeamIds), [selectedTeamIds]);
  const userSelected = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const filteredTeams = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return teams;
    return teams.filter((teamObj) => (teamObj.name || '').toLowerCase().includes(term));
  }, [teams, search]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(term)
      || (u.email || '').toLowerCase().includes(term)
    );
  }, [users, search]);

  const toggleTeam = (id) => {
    if (disabled) return;
    const next = new Set(teamSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange({ teamIds: Array.from(next), userIds: Array.from(userSelected) });
  };

  const toggleUser = (id) => {
    if (disabled) return;
    const next = new Set(userSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange({ teamIds: Array.from(teamSelected), userIds: Array.from(next) });
  };

  const removeTeamChip = (id) => {
    const next = Array.from(teamSelected).filter((x) => x !== id);
    onChange({ teamIds: next, userIds: Array.from(userSelected) });
  };
  const removeUserChip = (id) => {
    const next = Array.from(userSelected).filter((x) => x !== id);
    onChange({ teamIds: Array.from(teamSelected), userIds: next });
  };

  const selectedTeams = teams.filter((teamObj) => teamSelected.has(teamObj.id));
  const selectedUsers = users.filter((u) => userSelected.has(u.id));

  return (
    <div ref={containerRef} className="position-relative">
      {/* Selected chips */}
      {(selectedTeams.length > 0 || selectedUsers.length > 0) && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {selectedTeams.map((teamObj) => (
            <span
              key={`tchip-${teamObj.id}`}
              className="badge rounded-pill d-inline-flex align-items-center gap-1"
              style={{
                backgroundColor: 'rgba(13, 110, 253, 0.08)',
                color: '#0d6efd',
                fontSize: 12,
                padding: '4px 8px',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: teamObj.color || '#0E7C7B',
                  display: 'inline-block',
                }}
              />
              {teamObj.name}
              {!disabled && (
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  aria-label={t('shared.remove', 'Remove')}
                  style={{ fontSize: 8, padding: 0 }}
                  onClick={() => removeTeamChip(teamObj.id)}
                />
              )}
            </span>
          ))}
          {selectedUsers.map((u) => (
            <span
              key={`uchip-${u.id}`}
              className="badge rounded-pill d-inline-flex align-items-center gap-1"
              style={{
                backgroundColor: '#e9ecef',
                color: '#495057',
                fontSize: 12,
                padding: '4px 8px',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#dee2e6',
                  color: '#495057',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 600,
                }}
              >
                {initialsOf(u.name)}
              </span>
              {u.name}
              {!disabled && (
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  aria-label={t('shared.remove', 'Remove')}
                  style={{ fontSize: 8, padding: 0 }}
                  onClick={() => removeUserChip(u.id)}
                />
              )}
            </span>
          ))}
        </div>
      )}

      {/* Trigger / search input */}
      <div
        className={`d-flex align-items-center px-3 py-2 ${invalid ? 'border-danger' : ''}`}
        style={{
          border: `1px solid ${invalid ? '#dc3545' : '#ced4da'}`,
          borderRadius: 8,
          background: disabled ? '#f8f9fa' : '#fff',
          cursor: disabled ? 'not-allowed' : 'text',
          boxShadow: open ? '0 0 0 0.2rem rgba(13, 110, 253, 0.18)' : 'none',
          borderColor: open ? '#86b7fe' : invalid ? '#dc3545' : '#ced4da',
        }}
        onClick={() => { if (!disabled) setOpen(true); }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t('workOrder.assignTo.placeholder', 'Type name or email address')}
          className="form-control border-0 p-0"
          style={{ boxShadow: 'none', background: 'transparent' }}
          disabled={disabled}
        />
        <FeatherIcon icon={open ? 'chevron-up' : 'chevron-down'} size={16} className="text-muted" />
      </div>

      {/* Dropdown panel */}
      {open && !disabled && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm"
          style={{ top: 'calc(100% + 4px)', maxHeight: 360, overflowY: 'auto', zIndex: 1050 }}
        >
          {/* Teams */}
          {filteredTeams.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                {t('teams.title', 'Teams')}
              </div>
              {filteredTeams.map((teamObj) => {
                const checked = teamSelected.has(teamObj.id);
                return (
                  <label
                    key={`team-${teamObj.id}`}
                    className="d-flex align-items-center gap-2 px-3 py-2 mb-0"
                    style={{ cursor: 'pointer' }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: teamObj.color || '#0E7C7B',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {initialsOf(teamObj.name)}
                    </span>
                    <span className="flex-grow-1 text-truncate">{teamObj.name}</span>
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={checked}
                      onChange={() => toggleTeam(teamObj.id)}
                    />
                  </label>
                );
              })}
            </>
          )}

          {/* Users */}
          {filteredUsers.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                {t('shared.users', 'Users')}
              </div>
              {filteredUsers.map((u) => {
                const checked = userSelected.has(u.id);
                return (
                  <label
                    key={`user-${u.id}`}
                    className="d-flex align-items-center gap-2 px-3 py-2 mb-0"
                    style={{ cursor: 'pointer' }}
                  >
                    {u.profileUrl ? (
                      <img
                        src={u.profileUrl}
                        alt={u.name}
                        width={28}
                        height={28}
                        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      />
                    ) : (
                      <span
                        aria-hidden
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: '#e9ecef',
                          color: '#495057',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {initialsOf(u.name)}
                      </span>
                    )}
                    <span className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="text-truncate">{u.name}</div>
                      {u.email && (
                        <div className="text-muted small text-truncate">{u.email}</div>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={checked}
                      onChange={() => toggleUser(u.id)}
                    />
                  </label>
                );
              })}
            </>
          )}

          {filteredTeams.length === 0 && filteredUsers.length === 0 && (
            <div className="text-muted text-center py-3 small">
              {t('workOrder.assignTo.noResults', 'No teams or users match your search.')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrderAssignToPicker;
